import { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import ChatMessage from "@/components/ChatMessage";
import TypingIndicator from "@/components/TypingIndicator";
import NearbyBookstores from "@/components/NearbyBookstores";
import MoodChips from "@/components/MoodChips";
import { streamChat } from "@/lib/chat";
import { toast } from "sonner";

type Msg = { role: "user" | "assistant"; content: string };

const WELCOME_MESSAGE: Msg = {
  role: "assistant",
  content:
    "Hey! 📚 Pull up a chair. So — what have you been reading lately? Or if nothing's grabbed you in a while, tell me about the last book that really stuck with you. I've got some thoughts.",
};

export default function Index() {
  const [messages, setMessages] = useState<Msg[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || isLoading) return;

    setHasInteracted(true);
    const userMsg: Msg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    setTimeout(() => inputRef.current?.focus(), 0);

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && prev.length > 1 && prev[prev.length - 2]?.role === "user" && prev[prev.length - 2]?.content === text) {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      const allMessages = [...messages.slice(1), userMsg];
      await streamChat({
        messages: allMessages,
        onDelta: (chunk) => upsertAssistant(chunk),
        onDone: () => setIsLoading(false),
        onError: (err) => {
          toast.error(err);
          setIsLoading(false);
        },
      });
    } catch {
      toast.error("Connection error. Please try again.");
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleMoodSelect = (prompt: string) => {
    send(prompt);
  };

    return (
    <main className="bookstore-grain flex flex-1 flex-col" role="main" aria-label="Indie book discovery chat">
      {/* Chat area */}
      <div
        ref={scrollRef}
        className="bookstore-vignette chat-scrollbar flex-1 overflow-y-auto px-4 py-6"
      >
        <div className="mx-auto flex max-w-4xl flex-col gap-5">
          {/* Hero header */}
          <div className="mb-6 flex flex-col items-center gap-5 pt-4">
            <motion.img
              src="/logo.png"
              alt="Shhh... Book Club"
              className="h-52 w-52 rounded-3xl object-contain"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
            <motion.h1
              className="font-display text-4xl font-extrabold text-foreground md:text-5xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
            >
              The Door's Open
            </motion.h1>
            <motion.p
              className="mx-auto max-w-lg text-center text-lg leading-relaxed text-muted-foreground"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
            >
              Rows of indie authors. Quiet corners. Books that don't get the spotlight they deserve. Take your time — we'll help you find something good.
            </motion.p>

            {/* Shelf divider */}
            <div className="shelf-line my-2 w-full max-w-md" />

            {/* Mood-based entry — hide after first interaction */}
            {!hasInteracted && (
              <motion.div
                className="w-full max-w-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <p className="mb-4 text-center text-sm font-medium text-muted-foreground">
                  Or if you'd rather just browse the vibes:
                </p>
                <MoodChips onSelect={handleMoodSelect} disabled={isLoading} />
              </motion.div>
            )}
          </div>

          {messages.map((msg, i) => (
            <ChatMessage key={i} role={msg.role} content={msg.content} />
          ))}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <TypingIndicator />
          )}

          {/* Nearby bookstores — shown after first interaction */}
          {messages.length > 2 && (
            <NearbyBookstores />
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-border/50 bg-background/80 backdrop-blur-lg">
        <div className="mx-auto flex max-w-4xl items-end gap-2 px-4 py-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Tell us what you're in the mood for..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-input bg-card px-4 py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            disabled={isLoading}
          />
          <Button
            onClick={() => send()}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-11 w-11 shrink-0 rounded-xl"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>
    </main>
  );
}
